/**
 * SolversTab — 문제 상세 [학생] 탭. **이 문제를 받은 학생**을 보여 준다.
 *
 * 계약 `docs/problem-hub-v2/BE-20260911-reply-solvers-by-problem.md` ·
 * 설계 `docs/problem-hub-v2/0911_SPEC_problem-student-tab.html`.
 *
 * 「받은」 학생이다 — 채점 기록이 없어도 이 문제가 나간 학생은 목록에 든다(오너 지시, 회신 §0).
 * 그래서 탭 이름이 「푼 학생」이 아니라 「학생」이다.
 *
 * 틀은 [시험지] 탭(PaperUsageTab)을 그대로 따른다 — 오류 띠 · 불러오는 중 · 머리 줄 + [새로고침] · 잘림 띠 · 표.
 * 같은 상세 창의 두 목록 탭이 다른 얼굴이면 같은 종류의 화면으로 읽히지 않는다.
 * 이 탭도 문제가 바뀔 때만 리마운트된다(ProblemHub `key={s-${id}}`) — 채점을 마치고 돌아오면 낡을 수 있어
 * [새로고침]을 곁에 둔다.
 */
import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import SolverTable from './SolverTable';
import { fetchSolversForProblem, SOLVER_RESULT } from './solversApi';

/**
 * 범위 알약 — 학습 「전체 테스트」(StudentsHub/AssignmentFeedPanel)와 같은 낱말 · 같은 얼굴이다.
 * 서버가 범위를 안 실어 준 응답이면 그리지 않는다 — 지어내지 않는다.
 */
const SCOPE = {
  all: { text: '학원 전체', title: '이 목록은 학원 전체 학생입니다' },
  mine: { text: '담당 학생만', title: '이 목록은 담당 학생만입니다 — 서버가 계정에 따라 범위를 좁혔습니다' },
};

function SolversTab({ problemId, onOpenStudent = null }) {
  const { getAccessToken } = useAuth();
  const [data, setData] = useState(null); // { items, summary, scope, totalItems, truncated }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (problemId == null) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    fetchSolversForProblem(problemId, getAccessToken)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => {
        if (!alive) return;
        // 실패하면 지난 결과도 내린다 — 오류 띠 옆에 옛 목록이 남아 있으면 그것이 지금 것으로 읽힌다.
        setData(null);
        setError(e?.message || '이 문제를 받은 학생을 불러오지 못했습니다.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [problemId, reloadKey, getAccessToken]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  if (problemId == null) {
    return <div className="p-6 text-sm text-gray-400">문제를 선택하면 이 문제를 받은 학생을 볼 수 있습니다.</div>;
  }

  const s = data?.summary || null;
  const scope = SCOPE[data?.scope] || null;

  return (
    <div className="space-y-3 p-1">
      {error && (
        <div className="flex items-start gap-2 rounded-2xl bg-red-50 p-3 text-sm text-red-600">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span className="min-w-0 flex-1">{error}</span>
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            className="inline-flex shrink-0 items-center gap-1 text-xs text-red-500 underline transition-colors hover:text-red-700 disabled:opacity-50"
          >
            <RefreshCw className="size-3" />
            다시 시도
          </button>
        </div>
      )}

      {loading && data == null && !error && (
        <p className="text-xs text-gray-400">이 문제를 받은 학생을 찾는 중…</p>
      )}

      {data != null && (
        <>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400">
            {/* 수는 행을 세지 않고 서버 집계를 쓴다 — 페이지와 무관한 범위 전체라 목록이 잘려도 맞다(설계 §5).
                「채점 전」은 0 이어도 적는다 — 0 도 정보다. 결과 색은 표의 「최근 결과」 칸과 같은 한 벌이다. */}
            {s ? (
              <>
                <span>이 문제를 받은 학생 {s.students}명</span>
                <span aria-hidden="true">·</span>
                <span>채점 {s.graded}명(정답 <span className={SOLVER_RESULT.correct.cls}>{s.correct}</span> · 오답 <span className={SOLVER_RESULT.wrong.cls}>{s.wrong}</span> · 무응답 {s.unanswered})</span>
                <span aria-hidden="true">·</span>
                <span>채점 전 {s.ungraded}명</span>
              </>
            ) : (
              <span>이 문제를 받은 학생 {data.totalItems}명</span>
            )}
            {scope && (
              <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500" title={scope.title}>
                {scope.text}
              </span>
            )}
            <button
              type="button"
              onClick={reload}
              disabled={loading}
              className="inline-flex items-center gap-1 text-gray-500 underline transition-colors hover:text-gray-700 disabled:opacity-50"
            >
              <RefreshCw className={`size-3 ${loading ? 'animate-spin' : ''}`} />
              새로고침
            </button>
          </div>

          {data.truncated && (
            <div className="rounded-2xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-700">
              학생이 {data.totalItems}명이라 최근 출제순 {data.items.length}명까지만 받았습니다.
            </div>
          )}

          <SolverTable
            items={data.items}
            empty={<p className="text-sm text-gray-400">이 문제를 받은 학생이 없습니다.</p>}
            onSelect={onOpenStudent}
          />
        </>
      )}
    </div>
  );
}

export default SolversTab;
